import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ImageViewer = ({ imageUrl, filename = 'Invoice image', className = '' }) => {
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);

  if (!imageUrl) {
    return (
      <div className={`h-full flex flex-col bg-card ${className}`}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-2">
            <Icon name="Image" size={20} className="text-error" />
            <h3 className="text-sm font-medium text-foreground">Image Viewer</h3>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-3">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto" />
            <div>
              <h4 className="text-lg font-medium text-foreground">Unable to load image</h4>
              <p className="text-sm text-muted-foreground mt-1">No document URL was provided.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const zoomIn = () => setScale(prev => Math.min(prev + 0.25, 3));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setScale(1);
  const rotateClockwise = () => setRotation(prev => (prev + 90) % 360);
  const rotateCounterClockwise = () => setRotation(prev => (prev - 90 + 360) % 360);
  const resetRotation = () => setRotation(0);

  return (
    <div className={`h-full flex flex-col bg-card ${className}`}>
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <Icon name="Image" size={20} className="text-primary" />
          <h3 className="text-sm font-medium text-foreground">Image Viewer</h3>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
            iconName="ZoomOut"
            title="Zoom out"
          />
          <button
            onClick={resetZoom}
            className="px-2 py-1 text-xs font-mono bg-muted rounded border border-border hover:bg-muted/80 transition-smooth min-w-[3rem] text-center"
            title="Reset zoom"
          >
            {Math.round(scale * 100)}%
          </button>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3}
            iconName="ZoomIn"
            title="Zoom in"
          />
          <div className="flex items-center space-x-2 border-l border-border pl-2 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={rotateCounterClockwise}
              iconName="RotateCcw"
              title="Rotate counter-clockwise"
            />
            <button
              onClick={resetRotation}
              className="px-2 py-1 text-xs font-mono bg-muted rounded border border-border hover:bg-muted/80 transition-smooth"
              title="Reset rotation"
            >
              {rotation}&deg;
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={rotateClockwise}
              iconName="RotateCw"
              title="Rotate clockwise"
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-muted/30 p-4">
        <div className="min-h-full min-w-full flex justify-center">
          <img
            src={imageUrl}
            alt={filename}
            className="border border-border rounded shadow-moderate"
            style={{
              transform: `scale(${scale}) rotate(${rotation}deg)`,
              transformOrigin: 'center center'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
